import React from 'react';
import styled, { keyframes } from 'styled-components';
import { heroLineup } from '../../data/homepageCharacters';
import { publicCutoutUrl } from '../../data/characterCutouts';

/**
 * Hero character lineup.
 *
 * Six characters arranged in an overlapping row, center-heavy. The
 * generated PNGs are portrait 3:4 with transparent backgrounds and tight
 * crops (head + torso), so we size each cutout by HEIGHT and let its
 * natural aspect ratio decide the width. `object-position: bottom center`
 * anchors the character at the bottom of the slot so billing labels and
 * the data strip never collide with the face.
 *
 * We do NOT use `mix-blend-mode: multiply`. It was the original "red ink
 * wash" idea but in practice crushed contrast against the red-600 canvas
 * and the characters vanished. The red poster vibe is carried instead by
 * the canvas gradient, the grain overlay, and a warm amber rim-light
 * drop-shadow on each cutout.
 *
 * Billing labels sit INSIDE the lineup container (not bleeding past its
 * bottom edge) so they never collide with the DataStrip below.
 */

/**
 * Entrance animation. IMPORTANT: we only animate opacity here, NOT transform.
 * The Slot below uses its `transform` property to apply the per-character
 * scale/tilt/translate from slotConfig. If this keyframe also touched
 * transform, `animation-fill-mode: forwards` would override the Slot's
 * static transform and the scale/tilt (center-biggest, outers-tilted) would
 * vanish the moment the animation finished. Opacity-only is the safe pattern.
 */
const floatIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const LineupContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: 100%;
  /* Reserve a strip below the character row for billing labels (NAME +
     role). The labels are positioned with a negative bottom offset so they
     sit INSIDE this padding zone (never past the container) while the
     characters above them touch the slot's bottom edge. */
  padding-bottom: 68px;
  pointer-events: none;
`;

/**
 * Each character gets a slot with a specific scale, z-index and tilt.
 * Indexes map to visual position (left → right).
 */
const slotConfig = [
    { scale: 0.95, z: 1, tilt: -3, ty: '1%'   }, // 0 far left
    { scale: 0.98, z: 2, tilt: -1, ty: '0.5%' }, // 1 mid-left
    { scale: 1.02, z: 4, tilt: 0,  ty: '0'    }, // 2 center hero (slight lift)
    { scale: 1.00, z: 3, tilt: 1,  ty: '0.5%' }, // 3 mid-right
    { scale: 0.98, z: 2, tilt: 2,  ty: '0.5%' }, // 4 right
    { scale: 0.95, z: 1, tilt: 4,  ty: '1%'   }, // 5 far right
];

const Slot = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  /* narrow flex basis so characters pack tightly and overlap */
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  /* critical: allow the wide landscape images to overflow into neighbours
     so the row reads as overlapping posters, not six thumbnails in a line. */
  overflow: visible;
  z-index: ${p => p.$z};
  transform: translateY(${p => p.$ty}) rotate(${p => p.$tilt}deg) scale(${p => p.$scale});
  transform-origin: bottom center;

  opacity: 0;
  animation: ${floatIn} 680ms var(--ease-out) forwards;
  animation-delay: ${p => p.$delay}ms;

  /* drop the two outer slots on medium screens, then the two next-outer
     on small screens. Keeps the center 2 (hero + sidekick) on phones. */
  @media (max-width: 1100px) {
    &:first-child,
    &:last-child {
      display: none;
    }
  }

  @media (max-width: 720px) {
    &:nth-child(2),
    &:nth-child(5) {
      display: none;
    }
  }
`;

const Cutout = styled.img`
  display: block;
  /* Size by HEIGHT. The 3:4 portrait cutouts at height 100% produce a
     width of 0.75 x slot_height, which deliberately overflows neighbouring
     slots for the mass-poster overlap effect.

     Three per-character knobs (all applied via transform so they compose
     with the Slot's tilt/slot-scale without fighting object-fit):

       $cutoutScale     - size multiplier (default 1)
       $cutoutTranslateY - vertical offset (default '0%', positive = down).
                           Needed for non-3:4 sources where the subject is
                           centered in the frame (Devasena is 1:1 with ~70%
                           of the canvas as transparent padding), so we
                           push the element DOWN to re-anchor the feet to
                           the slot's bottom edge.

     Origin is bottom-center so scaling grows the character UPWARD from the
     visual floor instead of cropping the head. */
  height: 100%;
  width: auto;
  max-width: 260%;
  object-fit: contain;
  object-position: bottom center;
  transform:
    translateY(${p => p.$cutoutTranslateY ?? '0%'})
    scale(${p => p.$cutoutScale ?? 1});
  transform-origin: bottom center;

  /* Rim light so the cutouts lift off the red canvas without a blend mode.
     drop-shadow respects alpha (unlike box-shadow) so transparent PNGs
     keep crisp silhouettes. */
  filter:
    drop-shadow(0 12px 28px rgba(8, 7, 10, 0.6))
    drop-shadow(0 0 1px rgba(255, 177, 60, 0.3));

  user-select: none;
  -webkit-user-drag: none;
`;

const Billing = styled.div`
  position: absolute;
  /* Sit the label in the container's reserved strip BELOW the slot, so it
     never paints on top of the character. -52px puts the top of the
     two-line (NAME + role) label about 20px below the slot bottom edge. */
  bottom: -52px;
  left: 50%;
  transform: translateX(-50%) rotate(${p => -p.$tilt}deg); /* un-tilt labels */
  text-align: center;
  pointer-events: none;
  white-space: nowrap;
  /* billing labels always sit above any neighbour's overflowing image */
  z-index: 10;

  @media (max-width: 1100px) {
    ${Slot}:first-child &,
    ${Slot}:last-child & {
      display: none;
    }
  }
`;

const BillingName = styled.div`
  font-family: var(--font-heading);
  font-weight: var(--weight-bold);
  font-size: var(--size-xs);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--color-cream-100);
  line-height: 1;
  text-shadow: 0 1px 4px rgba(8, 7, 10, 0.7);
`;

const BillingRole = styled.div`
  font-family: var(--font-body);
  font-weight: var(--weight-medium);
  font-size: 10px;
  color: var(--color-amber-500);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  line-height: 1;
  margin-top: 4px;
  text-shadow: 0 1px 4px rgba(8, 7, 10, 0.7);
`;

export const CharacterLineup = () => {
    return (
        <LineupContainer aria-hidden="true">
            {heroLineup.map((char, idx) => {
                const cfg = slotConfig[idx] || slotConfig[slotConfig.length - 1];
                // stagger from center outward: 2 → 1,3 → 0,4 → 5
                const order = Math.abs(idx - 2);
                const delay = 100 + order * 90;
                return (
                    <Slot
                        key={char.id}
                        $z={cfg.z}
                        $scale={cfg.scale}
                        $tilt={cfg.tilt}
                        $ty={cfg.ty}
                        $delay={delay}
                    >
                        <Cutout
                            src={publicCutoutUrl(char.cutoutUrl)}
                            alt="" // decorative; name is in the billing label
                            loading="eager"
                            draggable={false}
                            $cutoutScale={char.cutoutScale ?? 1}
                            $cutoutTranslateY={char.cutoutTranslateY ?? '0%'}
                        />
                        <Billing $tilt={cfg.tilt}>
                            <BillingName>{char.name}</BillingName>
                            {char.billing && <BillingRole>{char.billing}</BillingRole>}
                        </Billing>
                    </Slot>
                );
            })}
        </LineupContainer>
    );
};

export default CharacterLineup;
